<template>
  <Combobox as="div" v-model="selectedOption">
    <ComboboxLabel class="block text-sm/6 font-bold text-gray-900" v-show="label" :id="`${id}-label`" :for="id">
      {{ label }}
    </ComboboxLabel>
    <div class="relative mt-2 grid grid-cols-1">
      <ComboboxButton v-if="disabledInput">
        <ComboboxInput
          ref="comboInputRef"
          @keydown="handleKeydown"
          :id="id"
          :aria-labelledby="label ? `${id}-label` : undefined"
          :aria-label="!label ? ariaLabel || placeholder : undefined"
          :placeholder="placeholder"
          :readonly="disabledInput"
          v-bind="$attrs"
          class="block w-full rounded-md bg-white py-3 pl-3 pr-12 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
          :class="customClass"
          @input="query = $event.target.value"
          @blur="query = ''"
          :display-value="(selectedItem) => query || selectedItem?.data"
        />
      </ComboboxButton>
      <ComboboxInput
        ref="comboInputRef"
        @keydown="handleKeydown"
        v-else
        :id="id"
        :aria-labelledby="label ? `${id}-label` : undefined"
        :aria-label="!label ? ariaLabel || placeholder : undefined"
        :placeholder="placeholder"
        :readonly="disabledInput"
        v-bind="$attrs"
        class="block w-full rounded-md bg-white py-3 pl-3 pr-12 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
        :class="customClass"
        @input="query = $event.target.value"
        @blur="query = ''"
        :display-value="(selectedItem) => query || selectedItem?.data"
      />
      <ComboboxButton
        aria-label="combobox-button"
        class="absolute inset-y-0.5 right-0.5 flex items-center rounded-r-md px-2 focus:outline-none"
        v-if="!noDropdown"
      >
        <ChevronDownIcon class="size-5 text-gray-400" aria-hidden="true" />
      </ComboboxButton>
      <ComboboxOptions
        v-if="filteredList.length > 0"
        class="absolute z-10 mt-12 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
      >
        <ComboboxOption
          v-for="listItem in filteredList"
          :key="listItem.id"
          :value="listItem"
          as="template"
          v-slot="{ active, selected }"
        >
          <li
            :class="[
              'relative cursor-default select-none py-2 pl-3 pr-9',
              active ? 'bg-gray-100 text-pdsPrimaryColor outline-none' : 'text-pdsPrimaryColor'
            ]"
          >
            <span :class="['block truncate', selected && 'font-semibold']" v-if="!multiText">
              {{ listItem.data }}
            </span>

            <span :class="['block truncate', selected && 'font-semibold']" v-if="multiText">
              <div class="text-gray-900 font-bold pb-2">{{ listItem.text }}</div>
              <div>{{ listItem.subText }}</div>
              <ChevronRightIcon
                class="absolute right-0 top-1/2 transform -translate-y-1/2 size-5 text-gray-400 mr-2"
                aria-hidden="true"
              />
            </span>
            <span
              v-if="selected"
              :class="[
                'absolute inset-y-0 right-0 flex items-center pr-4',
                active ? 'text-white' : 'text-pdsPrimaryColor'
              ]"
            >
              <CheckIcon class="size-5" aria-hidden="true" />
            </span>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </div>
    <div
      v-show="showError"
      class="mt-2 text-sm text-red-600 h-3"
      :class="{
        'opacity-0': !errorMessage,
        'opacity-100': errorMessage,
        'transition-opacity duration-200': true
      }"
    >
      {{ message }}
    </div>
  </Combobox>
</template>

<script>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { CheckIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/vue/20/solid';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/vue';
import debounce from 'lodash/debounce';

export default {
  name: 'ComboBoxes',
  components: {
    CheckIcon,
    ChevronRightIcon,
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxLabel,
    ComboboxOption,
    ComboboxOptions,
    ChevronDownIcon
  },
  inheritAttrs: true,
  props: {
    label: {
      type: String,
      default: null
    },
    list: {
      required: true,
      type: Array
    },
    modelValue: { type: [String, Number, Object] },
    message: {
      type: String,
      default: 'Geen resultaten gevonden.'
    },
    multiText: {
      type: Boolean,
      default: false
    },
    customClass: {
      default: '',
      type: String
    },
    noDropdown: {
      default: false,
      type: Boolean
    },
    disabledInput: {
      default: false,
      type: Boolean
    },
    showError: {
      default: true,
      type: Boolean
    },
    noSearchList: {
      default: false,
      type: Boolean
    },
    id: {
      type: String,
      default: 'comboBoxes'
    },
    placeholder: {
      type: String,
      default: 'Kies of voer een waarde in'
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const query = ref('');
    const selectedOption = ref(props.modelValue || null);
    const filteredList = ref(props.list);
    const comboInputRef = ref(null);
    // Debounced search function
    const updateFilteredList = debounce(() => {
      filteredList.value =
        query.value === ''
          ? props.list
          : props.noSearchList
          ? props.list
          : props.list.filter((listItem) => {
              return listItem.data.toLowerCase().includes(query.value.toLowerCase());
            });
    }, 200);

    // Compute the error message state based on filteredList length
    const errorMessage = computed(() => query.value !== '' && filteredList.value.length === 0);

    const blockEnter = (e) => {
      if (e.key === 'Enter') {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };

    onMounted(() => {
      const el = comboInputRef.value?.$el ?? comboInputRef.value;
      if (el) {
        el.addEventListener('keydown', blockEnter, { capture: true });
      }
    });

    onBeforeUnmount(() => {
      const el = comboInputRef.value?.$el ?? comboInputRef.value;
      if (el) {
        el.removeEventListener('keydown', blockEnter, { capture: true });
      }
    });

    // Watch query changes and update list asynchronously
    watch(
      [query, () => props.list],
      () => {
        requestAnimationFrame(() => {
          updateFilteredList();
        });
      },
      { deep: true }
    );

    // Watch for changes in selectedOption and emit the update event
    watch(
      () => props.modelValue,
      (newVal) => {
        selectedOption.value = newVal;
      }
    );

    watch(selectedOption, (newVal) => {
      emit('update:modelValue', newVal);
      if (newVal?.id) {
        query.value = '';
      }
    });

    return {
      query,
      selectedOption,
      filteredList,
      errorMessage,
      comboInputRef
    };
  }
};
</script>
